/**
 * BuyContentGate.jsx — paywall shown by Reader/Player when content has a
 * `price_coins` tag and the viewer hasn't purchased it yet.
 *
 * Two purchase paths:
 *   1. Spend coins  — POST /content-pricing/{id}/buy
 *   2. Pay with card / Naira — POST /payments/book/checkout (Stripe / Paystack)
 *
 * The fiat path derives price from price_coins at the canonical
 * 100 coins = £1.00 ratio so admins/writers only set ONE number.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Coins, Lock, Loader2, CreditCard } from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import { PAYMENTS_DISABLED, PAYMENTS_UNAVAILABLE_MESSAGE } from '../utils/paymentAvailability';
import GatewayPicker from './GatewayPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const COINS_PER_GBP = 100;

const BuyContentGate = ({ contentId, children }) => {
  const [state, setState] = useState({ loading: true, access: null, error: '' });
  const [buying, setBuying] = useState(false);
  const [buyError, setBuyError] = useState('');
  const [fiatBusy, setFiatBusy] = useState(false);
  const [tipDraft, setTipDraft] = useState('');
  const [gateway, setGateway] = useState(null);

  const loadAccess = useCallback(async () => {
    if (!contentId) {
      setState({ loading: false, access: { unlocked: true, free: true }, error: '' });
      return;
    }
    try {
      const res = await authFetch(`${BACKEND_URL}/api/content-pricing/${encodeURIComponent(contentId)}/access`);
      if (!res.ok) {
        // 404 means this row has never had pricing data — treat as free so
        // we never block legacy content.
        setState({ loading: false, access: { unlocked: true, free: true }, error: '' });
        return;
      }
      const data = await res.json();
      setState({ loading: false, access: data, error: '' });
    } catch {
      // Network error: don't block. The Reader/Player has its own error UI.
      setState({ loading: false, access: { unlocked: true, free: true }, error: '' });
    }
  }, [contentId]);

  useEffect(() => {
    loadAccess();
  }, [loadAccess]);

  const handleBuyCoins = async () => {
    setBuying(true);
    setBuyError('');
    try {
      const res = await authFetch(
        `${BACKEND_URL}/api/content-pricing/${encodeURIComponent(contentId)}/buy`,
        { method: 'POST' }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = data.detail;
        if (detail && typeof detail === 'object' && detail.error === 'insufficient_coins') {
          setBuyError(`You need ${detail.required} coins. You have ${detail.balance}. Top up below or pay with card.`);
        } else if (typeof detail === 'string') {
          setBuyError(detail);
        } else {
          setBuyError(`Purchase failed (HTTP ${res.status})`);
        }
        return;
      }
      // Refresh access so the gate dissolves and children render.
      await loadAccess();
    } catch (e) {
      setBuyError(typeof e?.message === 'string' ? e.message : 'Network error');
    } finally {
      setBuying(false);
    }
  };

  const handleBuyFiat = async () => {
    if (PAYMENTS_DISABLED) {
      setBuyError(PAYMENTS_UNAVAILABLE_MESSAGE);
      return;
    }
    setFiatBusy(true);
    setBuyError('');
    try {
      const res = await authFetch(`${BACKEND_URL}/api/payments/book/checkout`, {
        method: 'POST',
        body: JSON.stringify({
          content_id: contentId,
          origin_url: window.location.origin,
          provider: gateway?.provider,
          is_diaspora_card: !!gateway?.is_diaspora_card,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.checkout_url) {
        setBuyError(typeof data?.detail === 'string' ? data.detail : 'Checkout could not be started');
        setFiatBusy(false);
        return;
      }
      window.location.href = data.checkout_url;
    } catch (e) {
      setBuyError(typeof e?.message === 'string' ? e.message : 'Network error');
      setFiatBusy(false);
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" data-testid="buygate-loading" />
      </div>
    );
  }

  if (state.access?.unlocked) {
    return children;
  }

  const price = state.access?.price_coins;
  const priceGbpRaw = state.access?.price_gbp ?? (price ? price / COINS_PER_GBP : null);
  const priceGbp = priceGbpRaw != null ? Number(priceGbpRaw).toFixed(2) : null;
  const priceNgn = state.access?.price_ngn;
  return (
    <div
      className="max-w-xl mx-auto my-8 rounded-2xl border border-amber-400/40 bg-gradient-to-br from-slate-900 to-amber-950/30 p-8 text-center shadow-2xl"
      data-testid="buygate-paywall"
    >
      <div className="inline-flex w-14 h-14 rounded-full bg-amber-500/20 items-center justify-center mb-4">
        <Lock className="w-6 h-6 text-amber-300" />
      </div>
      <h2 className="text-2xl font-bold text-white mb-2" data-testid="buygate-title">
        Unlock this content
      </h2>
      <p className="text-sm text-gray-400 mb-6">
        Pay with your in-app coins or with your card. One-time unlock — yours forever.
      </p>
      <div className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-amber-500/15 border border-amber-400/40 mb-2">
        <Coins className="w-5 h-5 text-amber-300" />
        <span className="text-2xl font-black text-amber-200 tabular-nums" data-testid="buygate-price">
          {price}
        </span>
        <span className="text-xs uppercase tracking-widest text-amber-300/80 font-bold">coins</span>
        {priceGbp && (
          <span className="text-xs text-amber-300/70 ml-2 font-semibold">
            ≈ £{priceGbp}
          </span>
        )}
      </div>
      {/* Local-currency pill — surfaces the NGN BUY rate so Nigerian
          readers know exactly what they'll be charged at Paystack. */}
      {priceNgn != null && (
        <p className="text-[11px] text-slate-400 mb-6" data-testid="buygate-local-currency">
          Pay-by-card cost: <span className="text-emerald-300 font-bold">₦{priceNgn.toLocaleString()}</span>
          {' '}or <span className="text-emerald-300 font-bold">£{priceGbp}</span>
        </p>
      )}
      {/* Pay-more-than-advertised — buyers can tip the writer with extra
          coins. Hidden by default to keep the paywall clean; expands to a
          numeric input that the buy flow forwards as `paid_coins`. */}
      <div className="mb-6" data-testid="buygate-tip-row">
        <details className="text-left bg-slate-900/40 border border-slate-700 rounded-xl p-3">
          <summary className="cursor-pointer text-sm text-amber-200 font-bold inline-flex items-center gap-2">
            💛 Want to pay more? Support the writer
          </summary>
          <p className="text-[11px] text-slate-400 mt-2">
            Enter the total you'd like to pay (must be ≥ {price} coins / £{priceGbp}). The extra goes straight to the scribe's wallet.
          </p>
          {/* Quick-tip preset chips. Tapping any chip pre-fills the input
              with advertised + N coins. Coin conversion: £1 ≈ 33 coins,
              £3 ≈ 100, £5 ≈ 167 — using the same 3p/coin BUY rate. */}
          <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid="buygate-tip-presets">
            {[
              { gbp: 1, coins: 33 },
              { gbp: 3, coins: 100 },
              { gbp: 5, coins: 167 },
            ].map((preset) => {
              const total = price + preset.coins;
              const isActive = tipDraft !== '' && Number(tipDraft) === total;
              return (
                <button
                  key={preset.gbp}
                  type="button"
                  onClick={() => setTipDraft(String(total))}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-black border tabular-nums transition-all active:scale-95 ${
                    isActive
                      ? 'bg-amber-500/30 border-amber-400 text-amber-100 shadow-md shadow-amber-500/30'
                      : 'bg-slate-800/60 border-slate-700 text-slate-300 hover:border-amber-400/60 hover:text-amber-200'
                  }`}
                  data-testid={`buygate-tip-preset-${preset.gbp}`}
                >
                  +£{preset.gbp}
                </button>
              );
            })}
            {tipDraft !== '' && (
              <button
                type="button"
                onClick={() => setTipDraft('')}
                className="ml-1 text-[10px] text-slate-400 hover:text-rose-300 underline"
                data-testid="buygate-tip-clear"
              >
                clear
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-amber-300 font-bold">🪙</span>
            <input
              type="number"
              inputMode="numeric"
              min={price}
              step={1}
              placeholder={`${price}`}
              value={tipDraft}
              onChange={(e) => setTipDraft(e.target.value)}
              className="w-28 px-2 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-amber-100 font-bold tabular-nums focus:border-amber-400 focus:outline-none"
              data-testid="buygate-tip-input"
            />
            <span className="text-[11px] text-slate-400 tabular-nums">
              {tipDraft && Number(tipDraft) > price
                ? `(£${(Number(tipDraft) * 0.03).toFixed(2)} · +${Number(tipDraft) - price} tip)`
                : '(coins to pay)'}
            </span>
          </div>
        </details>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button
          type="button"
          onClick={handleBuyCoins}
          disabled={buying || fiatBusy}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-slate-900 font-bold text-sm shadow-xl shadow-amber-500/30 transition-colors"
          data-testid="buygate-confirm"
        >
          {buying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />}
          {buying ? 'Spending coins…' : 'Pay with coins'}
        </button>
        <button
          type="button"
          onClick={handleBuyFiat}
          disabled={PAYMENTS_DISABLED || buying || fiatBusy}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 text-white font-bold text-sm shadow-xl shadow-indigo-500/30 transition-colors"
          data-testid="buygate-fiat"
        >
          {fiatBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
          {PAYMENTS_DISABLED
            ? 'Card payments unavailable'
            : fiatBusy
              ? 'Redirecting…'
              : `Pay with ${gateway?.provider ? gateway.provider.charAt(0).toUpperCase() + gateway.provider.slice(1) : 'card'}`}
        </button>
      </div>
      {/* Region-aware gateway picker — shows under the buttons so the
          "Pay with X" label flips when the user picks a different gateway. */}
      <div className="mt-4 max-w-md mx-auto text-left">
        {PAYMENTS_DISABLED ? (
          <p
            className="rounded-lg border border-amber-300/35 bg-amber-400/10 px-3 py-2 text-xs font-semibold text-amber-100"
            role="status"
            data-testid="buygate-payments-disabled"
          >
            {PAYMENTS_UNAVAILABLE_MESSAGE} Coin unlocks remain available.
          </p>
        ) : (
          <GatewayPicker onChange={setGateway} baseGbp={priceGbpRaw} compact />
        )}
      </div>
      {buyError && (
        <p className="mt-4 text-sm text-rose-300" data-testid="buygate-error">
          {buyError}
        </p>
      )}
    </div>
  );
};

export default BuyContentGate;

